/**
 * „Dodaj załącznik" over the composer: six tiles, and for the four cards a picker in the same box
 * (board C2). The picture and the film open the file dialog; the cards open their list, searchable,
 * and a row sends at once — the canon's „Wyślij" on hover says so before the press.
 *
 * Which tiles stand is the mobile app's `attachOptions`: the recording request needs a conversation
 * that names a client, and the plan and session lists read that client's own data.
 */

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import photoBox from '~/shared/ui/photoBox.module.css';

import { foldForSearch } from '../model/conversationRows';
import type { CardChoice } from '../model/chatSession';
import { usePickerRows, type PickerKind } from '../lib/usePickerRows';

import {
  BackGlyph,
  CalendarGlyph,
  CameraGlyph,
  CloseGlyph,
  DumbbellGlyph,
  ImageGlyph,
  LayersGlyph,
  SearchGlyph,
  VideoGlyph,
} from './ChatGlyphs';
import styles from './chat.module.css';

export type { PickerKind };

interface AttachPopoverProps {
  readonly customerId: number | null;
  readonly picker: PickerKind | null;
  readonly onPicker: (picker: PickerKind | null) => void;
  readonly onPickFiles: (accept: 'image' | 'video') => void;
  readonly onSendCard: (choice: CardChoice) => void;
  readonly onClose: () => void;
}

const PICKER_LIMIT = 80;

export function AttachPopover({ customerId, picker, onPicker, onPickFiles, onSendCard, onClose }: AttachPopoverProps) {
  const { t } = useTranslation();
  const tiles: { key: 'image' | 'video' | PickerKind; label: string; hint: string; icon: ReactNode }[] = [
    { key: 'image', label: t('chat.attach.image'), hint: t('web:chat.attach.hint.image'), icon: <ImageGlyph size={20} /> },
    { key: 'video', label: t('chat.attach.video'), hint: t('web:chat.attach.hint.video'), icon: <VideoGlyph size={20} /> },
    { key: 'exercise', label: t('chat.attach.exercise'), hint: t('web:chat.attach.hint.exercise'), icon: <DumbbellGlyph size={20} /> },
    ...(customerId === null
      ? []
      : ([
          { key: 'plan', label: t('chat.attach.plan'), hint: t('web:chat.attach.hint.plan'), icon: <LayersGlyph size={20} /> },
          { key: 'summary', label: t('chat.attach.summary'), hint: t('web:chat.attach.hint.summary'), icon: <CalendarGlyph size={20} /> },
          { key: 'clipRequest', label: t('chat.attach.clipRequest'), hint: t('web:chat.attach.hint.clipRequest'), icon: <CameraGlyph size={20} /> },
        ] as const)),
  ];

  return (
    <div className={styles.pop} role="dialog" aria-label={t('chat.attach.title')}>
      {picker === null ? (
        <>
          <div className={styles.popHead}>
            <span>{t('chat.attach.title')}</span>
            <button className={styles.popIcon} type="button" onClick={onClose} aria-label={t('web:chat.attach.close')}>
              <CloseGlyph />
            </button>
          </div>
          <div className={styles.tiles}>
            {tiles.map((tile) => (
              <button
                key={tile.key}
                className={styles.tile}
                type="button"
                onClick={() => {
                  if (tile.key === 'image' || tile.key === 'video') onPickFiles(tile.key);
                  else onPicker(tile.key);
                }}
              >
                <span className={styles.tileIcon}>{tile.icon}</span>
                <span className={styles.tileLabel}>{tile.label}</span>
                <span className={styles.tileHint}>{tile.hint}</span>
              </button>
            ))}
          </div>
          <div className={styles.popFoot}>{t('web:chat.attach.footer')}</div>
        </>
      ) : (
        <Picker
          kind={picker}
          customerId={customerId}
          onBack={() => {
            onPicker(null);
          }}
          onClose={onClose}
          onSendCard={onSendCard}
        />
      )}
    </div>
  );
}

function Picker({
  kind,
  customerId,
  onBack,
  onClose,
  onSendCard,
}: {
  readonly kind: PickerKind;
  readonly customerId: number | null;
  readonly onBack: () => void;
  readonly onClose: () => void;
  readonly onSendCard: (choice: CardChoice) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    field.current?.focus();
  }, [kind]);

  const { rows, pending, failed } = usePickerRows(kind, customerId);

  const needle = foldForSearch(query.trim());
  const shown = (needle === '' ? rows : rows.filter((row) => foldForSearch(row.title).includes(needle))).slice(0, PICKER_LIMIT);

  const title =
    kind === 'exercise'
      ? t('chat.picker.exerciseTitle')
      : kind === 'clipRequest'
        ? t('chat.picker.clipRequestTitle')
        : kind === 'plan'
          ? t('chat.picker.planTitle')
          : t('chat.picker.summaryTitle');

  const emptyKey = kind === 'plan' ? 'chat.picker.plansEmpty' : kind === 'summary' ? 'chat.picker.sessionsEmpty' : 'web:chat.picker.noMatch';

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const first = shown[0];
      if (first !== undefined) {
        event.preventDefault();
        onSendCard(first.choice);
      }
    }
  };

  return (
    <>
      <div className={styles.popHead}>
        <button className={styles.popIcon} type="button" onClick={onBack} aria-label={t('web:chat.attach.back')}>
          <BackGlyph />
        </button>
        <span>{title}</span>
        <button className={styles.popIcon} type="button" onClick={onClose} aria-label={t('web:chat.attach.close')}>
          <CloseGlyph />
        </button>
      </div>
      {kind === 'summary' ? <div className={styles.pickNote}>{t('web:chat.picker.sessionsNote')}</div> : null}
      <label className={styles.pickSearch}>
        <SearchGlyph size={15} />
        <span className={styles.sr}>{t('web:chat.picker.search')}</span>
        <input
          ref={field}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          onKeyDown={onKeyDown}
          placeholder={t('web:chat.picker.search')}
        />
      </label>
      <div className={styles.pickList} aria-busy={pending}>
        {pending ? <div className={styles.pickEmpty}>{t('web:chat.picker.loading')}</div> : null}
        {!pending && failed ? <div className={styles.pickEmpty}>{t('chat.picker.errorTitle')}</div> : null}
        {!pending && !failed && shown.length === 0 ? (
          <div className={styles.pickEmpty}>{t(rows.length === 0 ? emptyKey : 'web:chat.picker.noMatch')}</div>
        ) : null}
        {shown.map((row) => (
          <button
            key={row.key}
            className={styles.pickItem}
            type="button"
            onClick={() => {
              onSendCard(row.choice);
            }}
          >
            {kind === 'summary' ? null : typeof row.image === 'string' ? (
              <img className={`${String(photoBox.photo)} ${styles.pickThumb}`} src={row.image} alt="" loading="lazy" decoding="async" />
            ) : (
              <span className={`${styles.pickThumb} ${styles.thumbBlank}`} aria-hidden="true">
                {kind === 'plan' ? <LayersGlyph size={18} /> : <DumbbellGlyph size={18} />}
              </span>
            )}
            <span className={styles.pickText}>
              <span className={styles.pickTitle}>{row.title}</span>
              {row.meta === '' ? null : <span className={styles.pickMeta}>{row.meta}</span>}
            </span>
            <span className={styles.pickGo}>{t('web:chat.picker.send')}</span>
          </button>
        ))}
      </div>
    </>
  );
}
